import type { EnrollmentValidationError } from "@repo/models";

import {
	enumToOptions,
	getPropValidator,
	PushNotification,
	PushProcessingNotification,
	required,
	requiredIf,
	styles,
} from "@repo/helpers";
import { Button, ButtonTypes, DropDownList, MoveableDialog, TextArea, TextBox } from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { type ReactElement, type ReactNode, useMemo, useState } from "react";
import HtmlParser from "react-html-parser";

import EnrollmentValidationApi from "../../api/enrollmentValidationApi";
import { QUERY_KEYS } from "../../api/queryKeys";
import { useUserContext } from "../../contexts/userContext";
import { RepIncidentStatus } from "../../models/complaint";
import style from "./style.module.scss";

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	item: EnrollmentValidationError;
	/** Called after a successful save with the desired post-save action. */
	onSaved: (action: "next" | "open" | "close") => void | Promise<void>;
}

/** Add the <tbody> tag and drop extra whitespace so the parsed email html doesn't trip validateDOMNesting warnings. */
function sanitizeEmail(html: string) {
	return html
		.replace(/<table>\s*<tr>/gi, "<table><tbody><tr>")
		.replace(/<\/tr>\s*<\/table>/gi, "</tr></tbody></table>")
		.replace(/>\s*</gi, "><")
		.replace(/\s*</gi, "<")
		.replace(/>\s*/gi, ">");
}

function formatDate(date: Date | string | null | undefined) {
	if (!date) return "N/A";
	const d = new Date(date);
	if (isNaN(d.getTime())) return "N/A";
	return d.toLocaleDateString();
}

const validOptions = [
	{ label: "Yes", value: true },
	{ label: "No", value: false },
];

export function EnrollmentValidationReviewForm({ open, onOpenChange, item, onSaved }: Props) {
	const usersQuery = useQuery({
		queryKey: [QUERY_KEYS.EnrollmentValidations.Users],
		queryFn: () => EnrollmentValidationApi.getUsers(),
	});
	const user = useUserContext().user;

	const [qaAnalystId, setQaAnalystId] = useState<number | null>(item.qaAnalystId ?? user?.userId ?? null);
	const [valid, setValid] = useState<boolean | null>(item.valid ?? null);
	const [invalidReason, setInvalidReason] = useState<string>(item.invalidReason ?? "");
	const [status, setStatus] = useState<number>(item.statusId ?? 0);
	const [notes, setNotes] = useState<string>(item.notes ?? "");
	const [showEmails, setShowEmails] = useState(false);
	const [showAllEmails, setShowAllEmails] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const validValidator = getPropValidator(required("Valid"));
	const invalidReasonValidator = getPropValidator(requiredIf(valid === false, "Invalid Reason"));

	/** An invalid reason only means something on an invalid record, so drop it otherwise. */
	const validChanged = (v: boolean | null) => {
		setValid(v);
		if (v !== false) setInvalidReason("");
	};

	const handleSave = async (afterSave: () => void | Promise<void>) => {
		if (!item || isSaving) return;

		const validationError = validValidator(valid) || invalidReasonValidator(invalidReason);
		if (validationError) {
			PushNotification(`Validation Error - ${validationError}`);
			return;
		}

		const notify = PushProcessingNotification("Updating Enrollment Validation");

		try {
			await EnrollmentValidationApi.review({
				id: item.id,
				status,
				qaAnalystId,
				valid,
				invalidReason: valid === false ? invalidReason : "",
				notes,
			});
			await afterSave();
			notify.success("Enrollment Validation Updated Successfully");
		} catch (e) {
			console.error("Failed to save enrollment validation", e);
			notify.error("Failed to Update Enrollment Validation");
		} finally {
			setIsSaving(false);
		}
	};

	/** Parsing the raw email html is not cheap, and it only depends on the item. */
	const parsedEmailText = useMemo(() => {
		if (!item?.emailText) return [] as ReactElement[];

		const parsed = HtmlParser(sanitizeEmail(item.emailText));

		//Drop stray text nodes so the id lookup below always has props to read
		return (Array.isArray(parsed) ? parsed : [parsed]).filter(
			(node): node is ReactElement => !!node && typeof node === "object",
		);
	}, [item?.emailText]);

	const saveAndNext = () => handleSave(() => onSaved("next"));
	const saveAndOpen = () => handleSave(() => onSaved("open"));
	const saveAndClose = () => handleSave(() => onSaved("close"));

	const detail = (label: string, value: ReactNode) => (
		<div className={style.detailRow}>
			<span className={style.detailLabel}>{label}:</span>
			<span className={style.detailValue}>{value}</span>
		</div>
	);

	const footer = (
		<div className={style.dialogFooter}>
			<Button onClick={saveAndNext} types={[ButtonTypes.Primary]} disabled={isSaving}>
				Save and Next
			</Button>
			<Button onClick={saveAndOpen} types={[ButtonTypes.Primary]} disabled={isSaving}>
				Save and Open
			</Button>
			<Button onClick={saveAndClose} types={[ButtonTypes.Primary]} disabled={isSaving}>
				Save and Close
			</Button>
			<Button onClick={() => onOpenChange(false)} types={[ButtonTypes.Outline]} disabled={isSaving}>
				Close
			</Button>
		</div>
	);

	function renderEmailText() {
		if (!parsedEmailText.length) return null;

		return (
			<>
				{renderEmails()}
				{renderButtons()}
			</>
		);

		function renderButtons() {
			const showAllEmailsButton = showEmails && parsedEmailText.length > 1;
			return (
				<div className={style.emailActions}>
					<Button onClick={() => setShowEmails(!showEmails)} types={[ButtonTypes.Primary]}>
						{showEmails ? "Hide" : "Show"} Email Text
					</Button>
					{showAllEmailsButton && (
						<Button onClick={() => setShowAllEmails(!showAllEmails)} types={[ButtonTypes.Primary]}>
							Show {showAllEmails ? "Only Relevant" : "All"}
						</Button>
					)}
				</div>
			);
		}

		function renderEmails() {
			if (!showEmails) return null;
			if (showAllEmails) return parsedEmailText;
			const relevant = parsedEmailText.find((r) => String(r.props?.id) === String(item?.validationTypeId));
			//Nothing tagged for this validation type, so show what we do have rather than a blank panel
			return relevant ?? parsedEmailText;
		}
	}

	return (
		<MoveableDialog
			open={open}
			onOpenChange={(nextOpen) => {
				if (!isSaving) onOpenChange(nextOpen);
			}}
			title="Review Enrollment Validation"
			initialSize={{ width: 700, height: 1200 }}
			resizable
			movable
			footer={footer}
			className={styles("agent-portal-modal", style.noPadding)}
			contentClassName={style.noPadding}
		>
			<div className={style.content}>
				{!showEmails ? (
					<>
						{detail("Validation", item.validationName)}
						{detail("Date Of Error", formatDate(item.incidentDate))}
						{detail("Stopped Enrollment?", item.enrollmentStopped ? "Yes" : "No")}
						{detail("Deactivated Agent?", item.deactivated ? "Yes" : "No")}
						{detail("Enrollment ID", item.enrollmentId ?? "N/A")}
						{detail("Sale ID", item.saleId ?? "N/A")}
						{detail("Date Of Sale", formatDate(item.dateOfSale))}
						{detail("TPV Vendor", item.tpvVendor || "N/A")}
						{detail("TPV Disposition", item.tpvDisposition)}
						{detail("Number of Meters", item.numMeters)}
						{detail("Number of Duplicates", item.numDuplicates > 0 ? item.numDuplicates : "N/A")}
						{detail("Utility", item.utility)}
						{detail("Price", item.price)}
						{detail("Term", item.term)}
						{detail("TPV BTN", item.tpv_btn)}
						{detail("Account Number", item.accountNumber)}
						{detail("Auth Name", item.authName)}
						{detail("Service Address", item.serviceAddress)}
						{detail("Company Name", item.companyName)}
						{detail("Customer Name", item.customerName)}
						{detail("Machine Name", item.machineName)}

						<form className={style.form} onSubmit={(e) => e.preventDefault()}>
							<DropDownList
								id="evQAAnalystId"
								label="QA Analyst"
								value={qaAnalystId}
								changeHandler={setQaAnalystId}
								values={usersQuery.data ?? []}
								loaded={usersQuery.isFetched}
								clearable
								fullLength
								valueProp="Id"
								textProp="Name"
							/>
							<DropDownList
								id="evValid"
								label="Valid"
								value={valid}
								changeHandler={validChanged}
								values={validOptions}
								validator={validValidator}
								clearable
								fullLength
							/>
							<TextBox
								id="evInvalidReason"
								label="Invalid Reason"
								value={invalidReason}
								changeHandler={setInvalidReason}
								validator={invalidReasonValidator}
								hidden={valid === true || valid == null}
							/>
							<DropDownList
								id="evStatus"
								label="Status"
								value={status}
								changeHandler={setStatus}
								values={enumToOptions(RepIncidentStatus)}
								fullLength
							/>
							<TextArea
								id="evNotes"
								label="Notes"
								value={notes}
								rows={5}
								changeHandler={(v) => setNotes(v)}
							/>
						</form>
					</>
				) : null}
				{renderEmailText()}
			</div>
		</MoveableDialog>
	);
}
